import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState(15);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const timerRef = useRef(null);

  // Fetch session timeout and maintenance status from settings
  useEffect(() => {
    axios.get(`${API}/settings/session-timeout`).then((res) => {
      setSessionTimeout(res.data.session_timeout_minutes || 15);
    }).catch(() => {});
    axios.get(`${API}/settings/maintenance`).then((res) => {
      setMaintenanceMode(res.data.maintenance_mode || false);
    }).catch(() => {});
  }, []);

  const doLogout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // Global axios interceptor: handle kicked (401)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401 && token) {
          const detail = err?.response?.data?.detail || "";
          if (detail === "Session terminated by admin") {
            localStorage.removeItem("token");
            setToken(null);
            setUser(null);
            window.__sessionKicked = true;
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  // Activity-based session timeout
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!token) return;
    timerRef.current = setTimeout(() => {
      doLogout();
      window.__sessionExpired = true;
      window.location.href = "/login";
    }, sessionTimeout * 60 * 1000);
  }, [token, sessionTimeout, doLogout]);

  useEffect(() => {
    if (!token) return;
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    const handler = () => resetTimer();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [token, resetTimer]);

  const fetchMe = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setUser(res.data);
    } catch {
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchMe(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: tok, user: usr } = res.data;
    localStorage.setItem("token", tok);
    localStorage.setItem("lastUser", JSON.stringify({ name: usr.name, email: usr.email }));
    setToken(tok);
    setUser(usr);
    return usr;
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogleCredential = useCallback(async (access_token) => {
    const res = await axios.post(`${API}/auth/google/verify`, { access_token });
    const { token: tok, user: usr } = res.data;
    localStorage.setItem("token", tok);
    localStorage.setItem("lastUser", JSON.stringify({ name: usr.name, email: usr.email }));
    setToken(tok);
    setUser(usr);
    return usr;
  }, []);

  const logout = useCallback(() => {
    doLogout();
  }, [doLogout]);

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const updateTheme = useCallback(async (color) => {
    try {
      await axios.put(`${API}/auth/theme`, { theme_color: color }, { headers: { Authorization: `Bearer ${token}` } });
      setUser((prev) => prev ? { ...prev, theme_color: color } : prev);
    } catch {}
  }, [token]);

  const refreshUser = useCallback(() => {
    if (token) fetchMe(token);
  }, [token, fetchMe]);

  const hasPerm = useCallback((perm) => {
    return (user?.permissions || []).includes(perm);
  }, [user]);

  const value = useMemo(() => ({
    user, token, loading, login, loginWithGoogleCredential, logout, authHeader, API, sessionTimeout, updateTheme, refreshUser, hasPerm, maintenanceMode, setMaintenanceMode,
  }), [user, token, loading, login, loginWithGoogleCredential, logout, authHeader, sessionTimeout, updateTheme, refreshUser, hasPerm, maintenanceMode]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
